import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { selectedProfileIdAtom } from "../store";
import { api } from "@/api/instance";
import { useAuth } from "@/shared/lib/auth-context";
import { extractErrorMessage } from "../utils";
import { qk } from "@/shared/api/queryKeys";
import { moveProfileMeta } from "@/entities/profile";
import type { Profile } from "../types";

const PROFILE_TITLE_MAX_LEN = 64;

type ProfilesListResult = { profiles: Profile[] };

export function useProfileTitle(
  selectedProfileId: string,
  selectedProfileTitle: string,
) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [, setSelectedProfileId] = useAtom(selectedProfileIdAtom);

  const [isEditingProfileTitle, setIsEditingProfileTitle] = useState(false);
  const [draftProfileTitle, setDraftProfileTitle] = useState("");
  const [profileRenameError, setProfileRenameError] = useState<string | null>(
    null,
  );
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const [prevProfileId, setPrevProfileId] = useState(selectedProfileId);

  const renameMutation = useMutation({
    mutationFn: async ({
      profileKey,
      newTitle,
      nextKey,
    }: {
      profileKey: string;
      newTitle: string;
      nextKey: string;
    }) => {
      await api.v1.renameProfile(profileKey, { name: newTitle });
      return { profileKey, newTitle, nextKey };
    },
    onSuccess: ({ profileKey, newTitle, nextKey }) => {
      queryClient.setQueryData<ProfilesListResult>(qk.profiles.list(), (prev) => {
        if (!prev) return prev;
        return {
          profiles: prev.profiles.map((p) =>
            p.id === profileKey ? { ...p, id: nextKey, title: newTitle } : p,
          ),
        };
      });
      if (nextKey !== profileKey) {
        moveProfileMeta(queryClient, profileKey, nextKey);
      }
      setSelectedProfileId(nextKey);
      setIsEditingProfileTitle(false);
      setDraftProfileTitle("");
    },
    onError: (e) => {
      setProfileRenameError(
        extractErrorMessage(
          e,
          "Не удалось переименовать профиль. Попробуйте ещё раз.",
        ),
      );
    },
  });

  if (prevProfileId !== selectedProfileId) {
    setPrevProfileId(selectedProfileId);
    setIsEditingProfileTitle(false);
    setDraftProfileTitle("");
    setProfileRenameError(null);
  }

  useEffect(() => {
    if (!isEditingProfileTitle) return;
    const t = window.setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [isEditingProfileTitle]);

  const beginEditProfileTitle = useCallback(() => {
    setProfileRenameError(null);
    const segs = selectedProfileTitle.split("/");
    setDraftProfileTitle(
      (segs[segs.length - 1] ?? selectedProfileTitle).slice(
        0,
        PROFILE_TITLE_MAX_LEN,
      ),
    );
    setIsEditingProfileTitle(true);
  }, [selectedProfileTitle]);

  const cancelEditProfileTitle = useCallback(() => {
    setIsEditingProfileTitle(false);
    setDraftProfileTitle("");
    setProfileRenameError(null);
  }, []);

  const commitProfileTitle = useCallback(async () => {
    const username = auth.user?.username;
    const profileKey = selectedProfileId;
    const oldTitle = selectedProfileTitle;
    const nextSegment = draftProfileTitle.trim();

    if (!username || !profileKey) return;
    if (!nextSegment) {
      setProfileRenameError("Название профиля не может быть пустым.");
      return;
    }
    if (nextSegment.length > PROFILE_TITLE_MAX_LEN) {
      setProfileRenameError(
        `Максимальная длина названия — ${PROFILE_TITLE_MAX_LEN} символа.`,
      );
      return;
    }

    const parts = oldTitle.split("/");
    parts[parts.length - 1] = nextSegment;
    const newTitle = parts.join("/");

    if (newTitle === oldTitle) {
      cancelEditProfileTitle();
      return;
    }

    const currentProfiles =
      queryClient.getQueryData<ProfilesListResult>(qk.profiles.list())
        ?.profiles ?? [];
    if (currentProfiles.some((p) => p.title === newTitle)) {
      setProfileRenameError("Профиль с таким названием уже существует.");
      return;
    }

    setProfileRenameError(null);
    const keyIsName = profileKey === oldTitle;
    const nextKey = keyIsName ? newTitle : profileKey;
    try {
      await renameMutation.mutateAsync({ profileKey, newTitle, nextKey });
    } catch {
      // Inline error already set in mutation onError.
    }
  }, [
    auth.user?.username,
    cancelEditProfileTitle,
    draftProfileTitle,
    queryClient,
    renameMutation,
    selectedProfileId,
    selectedProfileTitle,
  ]);

  return {
    isEditingProfileTitle,
    draftProfileTitle,
    isRenamingProfile: renameMutation.isPending,
    profileRenameError,
    titleInputRef,
    setDraftProfileTitle,
    beginEditProfileTitle,
    cancelEditProfileTitle,
    commitProfileTitle,
  };
}
